// 1.5.A read-your-writes: the highest WAL LSN this tab wrote, sent as X-Min-LSN on REST reads for 30 s.
export const isReadYourWrites = (): boolean => process.env.NEXT_PUBLIC_READ_YOUR_WRITES === 'true';

export const WRITE_WINDOW_MS = 30_000;
const LSN_PATTERN = /^[0-9A-F]{1,8}\/[0-9A-F]{1,8}$/;

let maxLsn: string | null = null;
let lastWriteAt = 0;

// An LSN is 64-bit "hi/lo" in hex: compare hi, then lo (each fits a double).
const parts = (lsn: string) => lsn.split('/').map((h) => parseInt(h, 16));
const isAfter = (a: string, b: string) => {
  const [ah, al] = parts(a);
  const [bh, bl] = parts(b);
  return ah !== bh ? ah > bh : al > bl;
};

export function noteWriteLsn(lsn?: string | null): void {
  if (!lsn || !LSN_PATTERN.test(lsn)) return;
  if (!maxLsn || isAfter(lsn, maxLsn)) maxLsn = lsn;
  lastWriteAt = Date.now();
}

export function minLsnHeaders(): Record<string, string> {
  if (maxLsn && Date.now() - lastWriteAt > WRITE_WINDOW_MS) maxLsn = null;
  return maxLsn ? { 'X-Min-LSN': maxLsn } : {};
}

type LsnRequest = {
  method?: string;
  url?: string;
  headers: { set: (name: string, value: string) => unknown };
};

// Reads carry X-Min-LSN for 30 s after a write; writes ask main_service for X-Write-LSN.
export function withLsnHeaders<T extends LsnRequest>(config: T): T {
  if (!isReadYourWrites()) return config;
  if ((config.method || 'get').toLowerCase() === 'get') {
    Object.entries(minLsnHeaders()).forEach(([k, v]) => config.headers.set(k, v));
  } else {
    config.headers.set('X-Want-LSN', '1');
  }
  return config;
}

// Global axios also reaches S3 and Cloudinary: a custom header there fails the CORS preflight.
export function withLsnHeadersForOwnApi<T extends LsnRequest>(config: T): T {
  const base = process.env.NEXT_PUBLIC_API_URL;
  return base && (config.url || '').startsWith(`${base}/`) ? withLsnHeaders(config) : config;
}

export function noteResponseLsn<T extends { headers?: Record<string, any> }>(response: T): T {
  if (isReadYourWrites()) noteWriteLsn(response.headers?.['x-write-lsn']);
  return response;
}
